<script>
import { BeaconEvent } from '@airgap/beacon-sdk'
import { BeaconWallet } from '@taquito/beacon-wallet'
import { TezosToolkit } from '@taquito/taquito'
import io from 'socket.io-client'

import mainBody from './components/mainBody.vue'
import { reduceAddress } from './utilities'
import {
  APP_NAME,
  ADMIN_ADDRESS,
  BEACON_MATRIX_NODES,
  DEFAULT_GAME_SIZE,
  NODE_URL,
  SOCKET_URL,
  clearStaleBeaconStorage,
  getBeaconNetwork,
} from './constants'

// Wipe any cached Beacon relay entries that point at known-dead hosts
// BEFORE the SDK initialises. Must happen at module-load time so it runs
// before any DAppClient instance reads from localStorage.
clearStaleBeaconStorage()

// Beacon throws/rejects non-Error objects when the user dismisses a
// wallet popup (e.g. `{title: 'Aborted', description: '…'}`). Those
// rejections don't always reach the awaiting promise's catch (Beacon
// fires several internal subscribers as well), so webpack-dev-server's
// overlay shows them as `ERROR [object Object]`. We swallow the known
// abort variants and let real errors continue to surface.
if (typeof window !== 'undefined') {
  const isBeaconAbort = (r) => {
    if (!r) return false
    if (r.name === 'AbortedBeaconError') return true
    const probe = `${r.title || ''} ${r.description || ''} ${r.message || ''}`.toLowerCase()
    return /aborted|cancel|denied|user closed|user rejected/.test(probe)
  }
  // Some wallets (notably Kukai's hosted web wallet) don't implement the
  // `shadownet` network type even though beacon-sdk's enum declares it.
  // They reject the permission request with NetworkNotSupportedBeaconError.
  // That rejection escapes Beacon's internal subscribers as an uncaught
  // promise — swallow it and point the user at a wallet that works.
  const isBeaconNetworkUnsupported = (r) => {
    if (!r) return false
    if (r.name === 'NetworkNotSupportedBeaconError') return true
    const probe = `${r.title || ''} ${r.description || ''} ${r.message || ''}`.toLowerCase()
    return /network.*not.*support|not.*support.*network|network_not_supported/.test(probe)
  }
  window.addEventListener('unhandledrejection', (event) => {
    if (isBeaconAbort(event.reason)) {
      event.preventDefault()
      // Surface a quiet console note so devs can still see it happened.
      // eslint-disable-next-line no-console
      console.info('[beacon] user dismissed wallet popup — suppressing overlay')
    } else if (isBeaconNetworkUnsupported(event.reason)) {
      event.preventDefault()
      // eslint-disable-next-line no-console
      console.warn(
        '[beacon] wallet rejected the shadownet network. Kukai\'s web wallet ' +
        'does not support shadownet — use Temple (browser extension), which ' +
        'connects to custom/test networks.',
      )
    }
  })
  window.addEventListener('error', (event) => {
    if (
      isBeaconAbort(event.error) || isBeaconAbort(event) ||
      isBeaconNetworkUnsupported(event.error) || isBeaconNetworkUnsupported(event)
    ) {
      event.preventDefault()
    }
  })
}

const Tezos = new TezosToolkit(NODE_URL)

export default {
  name: 'TezosXTZLounge',
  components: { mainBody },
  data() {
    return {
      wallet: undefined,
      tezos: undefined,
      socket: undefined,
      walletAddress: undefined,
      user: '',
      gameSize: DEFAULT_GAME_SIZE,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    }
  },
  created() {
    this.socket = io(SOCKET_URL)
    this.tezos = Tezos
    this.getWallet()
    this.socket.on('socketId', (socketId) => {
      this.user = socketId
    })
  },
  mounted() {
    window.addEventListener('resize', this.onResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    async getWallet() {
      // Reuse an existing wallet if one was already created on this page.
      if (this.wallet) return this.wallet

      // Build the Beacon network config from constants. For mainnet the
      // SDK already knows the RPC, but we pass it explicitly anyway so
      // the dApp and the wallet are always agreeing on the same chain.
      const network = getBeaconNetwork()
      // matrixNodes override using the SDK's actual region keys
      // (Regions.* values from @airgap/beacon-types). We MUST override
      // because @taquito/beacon-wallet@21 pins @airgap/beacon-dapp@4.5.1,
      // which transitively installs beacon-transport-matrix@4.5.1 nested
      // under node_modules/@taquito/beacon-wallet/node_modules/. That
      // 4.5.1 P2PCommunicationClient has dead hostnames hardcoded as the
      // default regional list:
      //   north-america-east: beacon-node-1.beacon-server-1.papers.tech
      //   north-america-west: beacon-node-1.beacon-server-2.papers.tech
      //   asia-east:          beacon-node-1.beacon-server-3.papers.tech
      //   australia:          beacon-node-1.beacon-server-4.papers.tech
      // None of those resolve in DNS anymore. The SDK probes all regions
      // in parallel; the dead probes generate ERR_NAME_NOT_RESOLVED
      // errors and stall the 60s probe race, eventually surfacing as
      // "No server responded." from DAppClient.js.
      //
      // Spread-merge in the SDK is by-key, so passing the right region
      // keys REPLACES each region's list. We point every region at
      // octez.io hosts (Tezos Foundation-operated, currently live with
      // proper CORS) plus a couple healthy papers.tech for redundancy.
      const wallet = new BeaconWallet({
        name: APP_NAME,
        network,
        matrixNodes: BEACON_MATRIX_NODES,
      })
      this.wallet = wallet

      // Network-mismatch cleanup. If the cached active account was bound
      // to a different network than the one we just configured (e.g. the
      // user connected on shadownet before we retired the network toggle
      // and locked the dApp to mainnet), every signed op routes through
      // a wallet/network combo Kukai can't honor — the user sees a
      // "switch apps / switch network" prompt instead of a normal signing
      // flow because Beacon falls back to its wallet-selection dialog when
      // the cached account references a network the new permissions can't
      // serve.
      //
      // clearActiveAccount() alone isn't enough — Beacon also caches the
      // P2P peer registry + transport selection in localStorage. After a
      // network swap the peer entries point at relays that the new wallet
      // can't reach, so the next request triggers the "pick a wallet"
      // prompt even though clearActiveAccount succeeded. Full disconnect
      // (removeAllPeers + disconnect) wipes both layers; the user re-pairs
      // cleanly via the next "Sign in" click.
      try {
        const cached = await wallet.client.getActiveAccount()
        if (cached && cached.network?.type && cached.network.type !== network.type) {
          // eslint-disable-next-line no-console
          console.info(
            `[beacon] active account network "${cached.network.type}" ≠ configured "${network.type}" — full disconnect`
          )
          try { await wallet.client.removeAllPeers() } catch (_e) { /* ignore */ }
          try { await wallet.clearActiveAccount() } catch (_e) { /* ignore */ }
          try { await wallet.disconnect() } catch (_e) { /* ignore */ }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[beacon] network-mismatch cleanup failed (continuing):', e?.message)
      }

      wallet.client.subscribeToEvent(BeaconEvent.ACTIVE_ACCOUNT_SET, (account) => {
        this.broadcastWallet(account)
        // Skip walletConnection on disconnect — broadcastWallet already
        // emits 'newWallet' = 'SYNC WALLET' for the null path. The
        // server echoes whatever it receives as 'newWallet', so a null
        // here would clobber walletAddress and break .includes() in
        // mainBody's template (the v-if for the "Reset wallet" pill).
        if (account?.address) {
          this.socket.emit('walletConnection', account.address)
        }
        this.socket.emit('updateGames')
      })

      // Replace Beacon's default UI for these events with no-ops. The
      // default handler shows a blocking alert AND emits the same object
      // out as an error; subscribing here disables both. Our own
      // try/catch in startGameBC/continueBetBC/dealCard handles user-facing
      // status updates instead.
      //
      // GENERIC_ERROR is included because beacon-sdk re-runs init() on
      // session restore for non-p2p origins (extension, post-message);
      // if its parallel P2P transport connect fails (relay probe race
      // times out at 60s), DAppClient.js:589 emits GENERIC_ERROR with
      // err.message "No server responded." — which the default handler
      // surfaces as a modal alert with a Send Report button. The session
      // also gets torn down by abortHandler regardless. Swallowing here
      // hides the popup; the disconnect still happens (we don't control
      // the SDK's abort), but the noisy modal is gone.
      const swallowed = [
        BeaconEvent.OPERATION_REQUEST_ERROR,
        BeaconEvent.PERMISSION_REQUEST_ERROR,
        BeaconEvent.SIGN_REQUEST_ERROR,
        BeaconEvent.BROADCAST_REQUEST_ERROR,
        BeaconEvent.NO_PERMISSIONS,
        BeaconEvent.GENERIC_ERROR,
        BeaconEvent.UNKNOWN,
      ]
      for (const evt of swallowed) {
        if (evt) {
          try {
            wallet.client.subscribeToEvent(evt, (data) => {
              // eslint-disable-next-line no-console
              console.info(`[beacon] ${evt}:`, data)
            })
          } catch (_e) {
            // Not all SDK versions expose every event constant — skip
            // missing ones silently.
          }
        }
      }

      return wallet
    },
    async broadcastWallet(account) {
      if (!account) {
        this.socket.emit('newWallet', 'SYNC WALLET')
        this.socket.emit('updateGames')
        return
      }
      // Use the connected wallet as the signing provider. Beacon proxies signing
      // requests to the wallet; we should NOT instantiate a RemoteSigner here.
      this.tezos.setWalletProvider(this.wallet)
      const reducedAddress = await reduceAddress(account.address)
      this.socket.emit('newWallet', `UNSYNC WALLET ${reducedAddress}`)
    },
    async sendTezos(_activeAccount, amount) {
      this.tezos.setWalletProvider(this.wallet)
      await this.tezos.wallet.transfer({ amount, to: ADMIN_ADDRESS }).send()
    },
    onResize() {
      this.windowWidth = window.innerWidth
      this.socket.emit('resizeGame', window.innerWidth, this.socket.id)
    },
  },
}
</script>

<template>
  <div class="body">
    <mainBody :wallet="wallet" :socket="socket" :tezos="tezos" :gameSize="gameSize" />
  </div>
</template>

<style>
/* ─── Design tokens ─────────────────────────────────────────────────────
   Crypto/web3 dark aesthetic. Saturated gradient accents, violet hover
   glows, mixed type. These vars are consumed by .actionButton, .gameInfo,
   etc. in mainBody.vue, and by every component's scoped styles when they
   reference var(--ad-...). Tweak them here to retheme globally. */
/* ─── Theme: Imperial Bordeaux ──────────────────────────────────────
   Wine-dark base, burnished brass primary, jade-teal secondary. Warm
   ivory text instead of cool white. Replaces the prior navy / gold /
   violet palette.

   NAMING NOTE: token names (`--ad-gold-*`, `--ad-violet-*`) are
   preserved as semantic slots so existing components don't need to be
   re-themed one by one. The *values* are what changed. In this
   red/white sports palette:
       --ad-gold-*    → varsity red (primary CTA / accent)
       --ad-violet-*  → midnight navy / charcoal (secondary accent)
       --ad-red-*     → bright signal red for warnings (warmer than the
                        primary varsity red so they read distinct)
       --ad-green-*   → field green for positive state (kept)
   Background flips from wine-dark to off-white; text inverts from warm
   ivory to deep charcoal. Radii sharpened (smaller, more athletic) and
   the display font swapped to Oswald so headers carry the varsity feel
   without each component needing a font override. */
:root {
  /* Surface — clean stadium white */
  --ad-bg-deep: #f6f3ee;
  --ad-bg-base: #ffffff;
  --ad-bg-elev-1: #f4f1ec;
  --ad-bg-elev-2: #ebe6dd;
  --ad-bg-elev-3: #ddd6ca;

  /* Borders — dark with alpha so they read against light surfaces */
  --ad-border-faint: rgba(20, 20, 20, 0.06);
  --ad-border-soft:  rgba(20, 20, 20, 0.12);
  --ad-border-mid:   rgba(20, 20, 20, 0.22);

  /* Text — deep charcoal, with stepped opacity for hierarchy */
  --ad-text-1: #14181d;
  --ad-text-2: rgba(20, 24, 29, 0.72);
  --ad-text-3: rgba(20, 24, 29, 0.46);

  /* Primary accent — varsity red (the "gold" slot keeps its name) */
  --ad-gold-1: #ff3d4d;   /* bright hover red */
  --ad-gold-2: #c8102e;   /* main varsity red */
  --ad-gold-3: #8b0a1f;   /* deep brick — pressed / dark accent */

  /* Secondary accent — midnight navy (the "violet" slot) */
  --ad-violet-1: #4a6fa5;
  --ad-violet-2: #15294a;
  --ad-violet-3: #0a1730;

  /* Status colors. Bright orange-red for warning so it reads distinct
     from the brand red; field green for positive. */
  --ad-red-1: #ff8a65;
  --ad-red-2: #d84315;
  --ad-green-1: #43a047;
  --ad-green-2: #1b5e20;

  /* Gradients — flat-ish, sports decals not casino metallics */
  --ad-grad-gold:   linear-gradient(135deg, #ff3d4d 0%, #c8102e 50%, #8b0a1f 100%);
  --ad-grad-violet: linear-gradient(135deg, #4a6fa5 0%, #15294a 100%);
  --ad-grad-fire:   linear-gradient(135deg, #ff3d4d 0%, #c8102e 60%, #8b0a1f 100%);
  --ad-grad-bg:     radial-gradient(ellipse at 50% -10%, rgba(200, 16, 46, 0.07) 0%, transparent 55%),
                    radial-gradient(ellipse at 100% 100%, rgba(21, 41, 74, 0.05) 0%, transparent 50%),
                    linear-gradient(180deg, #ffffff 0%, #f6f3ee 100%);

  /* Shadows / glows — softer drop, red glow for CTAs */
  --ad-shadow-card:  0 6px 18px rgba(20, 24, 29, 0.10), 0 1px 2px rgba(20, 24, 29, 0.06);
  --ad-glow-gold:    0 0 0 1px rgba(200, 16, 46, 0.55), 0 4px 14px rgba(200, 16, 46, 0.22);
  --ad-glow-violet:  0 0 0 1px rgba(21, 41, 74, 0.45), 0 4px 16px rgba(21, 41, 74, 0.20);

  /* Radius — sharper, more rectangular sports geometry */
  --ad-r-sm: 4px;
  --ad-r-md: 6px;
  --ad-r-lg: 10px;
  --ad-r-pill: 999px;

  /* Type — Oswald for display (headers, big numbers), Inter for body */
  --ad-font-display: 'Oswald', 'Inter', system-ui, sans-serif;
  --ad-font-body:    'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  --ad-font-mono:    'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

html, body {
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  background: var(--ad-bg-deep);
  color: var(--ad-text-1);
  font-family: var(--ad-font-body);
  font-feature-settings: 'cv11', 'ss01';
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

body {
  /* Stadium-white backdrop with a hint of red wash at the top + navy
     wash at the bottom-right. Subtle enough to read as "clean white
     page" but warm enough to not feel sterile. */
  min-height: 100vh;
  background: var(--ad-grad-bg);
  background-attachment: fixed;
  padding: 16px 0;
  /* Thin red varsity stripe across the very top of every page — the
     sports decal touch. Sits behind everything via box-shadow so we
     don't have to wrap content in another container. */
  box-shadow: inset 0 4px 0 0 var(--ad-gold-2);
}

#app {
  text-align: center;
  color: var(--ad-text-1);
  margin: 0;
  padding: 0;
}

/* Native form controls — modernize the defaults so even unstyled selects
   and inputs feel native to the light sports theme. */
input, select, textarea, button {
  font-family: inherit;
  color: inherit;
}
button { cursor: pointer; }

/* Crisper scrollbars on light surfaces. WebKit only — Firefox has its own. */
::-webkit-scrollbar { width: 10px; height: 10px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb {
  background: rgba(20, 24, 29, 0.18);
  border-radius: 999px;
}
::-webkit-scrollbar-thumb:hover { background: rgba(20, 24, 29, 0.32); }

/* ─── Global animations ──────────────────────────────────────────────
   Reusable keyframes + utility classes. Components opt-in via class. */

/* Smooth fade + lift on first paint or view-switch. */
@keyframes adFadeUp {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes adFadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes adShimmer {
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes adPulseDot {
  0%, 100% { transform: scale(1);   opacity: 1; }
  50%      { transform: scale(1.6); opacity: 0.55; }
}
@keyframes adGlowSoft {
  0%, 100% { box-shadow: 0 0 0 0 rgba(21, 41, 74, 0); }
  50%      { box-shadow: 0 0 0 6px rgba(21, 41, 74, 0.10); }
}

/* View container — every top-level app component gets a gentle entrance. */
.view-enter-active,
.view-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}
.view-enter-from { opacity: 0; transform: translateY(8px); }
.view-leave-to   { opacity: 0; transform: translateY(-8px); }

/* Network-badge dot pulse — applied via .networkBadge__dot in mainBody. */
.networkBadge__dot {
  animation: adPulseDot 2.4s ease-in-out infinite;
}

/* Generic action-button hover lift. Components can drop the class on
   anything clickable to get a consistent micro-interaction. */
.actionLift {
  transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.15s ease;
}
.actionLift:hover { transform: translateY(-2px); }
.actionLift:active { transform: translateY(0); }

/* Skeleton shimmer for loading states. */
.adSkel {
  background: linear-gradient(
    90deg,
    var(--ad-bg-elev-1) 0%,
    var(--ad-bg-elev-3) 50%,
    var(--ad-bg-elev-1) 100%
  );
  background-size: 200% 100%;
  animation: adShimmer 1.6s linear infinite;
  border-radius: var(--ad-r-sm);
}

/* Honor user's reduced-motion preference. */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001ms !important;
  }
}

/* ─── Mobile baseline ─────────────────────────────────────────────────
   App.vue scope: rules that need to win against everything, since the
   shell + game components inherit body styles. mainBody.vue carries
   the rest (button/nav touch targets). */

/* Belt-and-suspenders: prevent ANY child from causing the page to
   sideways-scroll on phones. Some game canvases briefly overflow during
   animation; without this they push the whole viewport. */
html, body {
  overflow-x: hidden;
  max-width: 100vw;
}
/* Phones: shrink ambient body padding, ensure tap-zoom doesn't kick in
   for input controls (font-size >= 16px is iOS's threshold). */
@media (max-width: 480px) {
  body { padding: 6px 0; }
  input, select, textarea {
    /* iOS Safari auto-zooms any focused control whose font-size is
       under 16px. Pinning to 16px keeps the viewport steady. */
    font-size: 16px;
  }
}
</style>
